package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry

import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.repository.ShiftRepository
import evoshift.composeapp.generated.resources.claim_pending
import evoshift.composeapp.generated.resources.schedule_swap_withdrawn
import kotlinx.datetime.toLocalDateTime
import evoshift.composeapp.generated.resources.claim_assigned
import evoshift.composeapp.generated.resources.schedule_hours_sent
import com.evoapps.evoshift.domain.model.ClaimOutcome
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_session_expired
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.workerId
import com.evoapps.evoshift.domain.model.TimeRange
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.marketplace.RequestShiftSwapUseCase
import com.evoapps.evoshift.domain.usecase.marketplace.TakeShiftFromPoolUseCase
import com.evoapps.evoshift.domain.usecase.roster.GetRosterShiftsUseCase
import com.evoapps.evoshift.domain.usecase.roster.GetRosterSummaryUseCase
import com.evoapps.evoshift.domain.usecase.shift.UpdateShiftUseCase
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.session.WorkplaceMonthSelection
import com.evoapps.evoshift.session.of
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.first
import com.evoapps.evoshift.domain.model.RosterIds
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import com.evoapps.evoshift.domain.model.SwapStatus
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.model.SwapPolicy
import com.evoapps.evoshift.domain.model.SwapRequest
import com.evoapps.evoshift.domain.usecase.finance.OwnMonthPayEstimate
import com.evoapps.evoshift.domain.usecase.finance.ShiftPayChangeKind

data class ScheduleUiState(
    val isLoading: Boolean = true,
    val error: UiText? = null,
    /** SH-168: the schedule could not be read (nothing shown): Try again instead of an empty month. */
    val loadError: UiText? = null,
    /** SH-168: a pull-to-refresh is reading the months and shifts again. */
    val isRefreshing: Boolean = false,
    /** A one-off confirmation (e.g. after taking a shift), shown in a snackbar. */
    val message: UiText? = null,
    val currentUserId: String = "",
    val currentWorkplaceId: String? = null,
    val availableWorkplaces: Map<String, String> = emptyMap(),

    val publishedRosters: List<Roster> = emptyList(),
    val currentRosterId: String? = null,

    val shifts: List<Shift> = emptyList(),
    val myShifts: List<Shift> = emptyList(),
    /** SH-210: the job interviews the person takes part in (every workplace), earliest first. */
    val myInterviews: List<com.evoapps.evoshift.domain.model.Interview> = emptyList(),
    /** SH-223: the person's own work requests (approved remote work and work events mark their shifts). */
    val myWorkRequests: List<com.evoapps.evoshift.domain.model.WorkRequest> = emptyList(),
    val marketplaceShifts: List<Shift> = emptyList(),
    val selectedDay: Int? = null,
    val selectedDayShifts: List<Shift> = emptyList(),
    val selectedShift: Shift? = null,
    val firstDayOfWeek: Int = 1,
    val assignableEmployees: List<User> = emptyList(),
    val showSwapDialog: Boolean = false,
    val swapTargetShiftId: String? = null,
    /** What the person may do at the workplace: trading and clocking shifts need [Gate.TRADE_SHIFTS] (not once the plan ended). */
    val capabilities: Set<Capability> = emptySet(),
    /**
     * SH-106: the workplace checks the location at clock-in, so only the clock button (through the
     * server) and the managers record times; the person cannot type their own.
     */
    val ownHoursByManager: Boolean = false,
    /** SH-118: the managers' notes on the person's own shifts, by shift id (only they and managers read them). */
    val shiftNotes: Map<String, String> = emptyMap(),
    /**
     * SH-165: the colleagues for "Swap with someone", best first (available and free that day
     * first); null while they load.
     */
    val swapCandidates: List<com.evoapps.evoshift.domain.usecase.marketplace.SwapCandidate>? = null,
    /** SH-165: the role the swap picker is limited to (the shift's), or null. */
    val swapRole: String? = null,
    /**
     * SH-165: the tapped day had exactly one shift of the person's own, opened directly; the day's
     * list stays behind it ("Show the whole day").
     */
    val dayListHidden: Boolean = false,
    /**
     * SH-165: how each shift would change the person's own estimated take-home pay for its month,
     * by shift id: their own shifts as given away or swapped, the marketplace's as taken. Empty
     * without pay terms they can read (nothing is shown then).
     */
    val payChanges: Map<String, com.evoapps.evoshift.domain.usecase.finance.ShiftPayChange> = emptyMap(),
    /** The workplace's currency, for the pay changes. */
    val currency: String = "PLN",
    /** SH-257: whether (and how far back) the person enters their own hours, and the workplace's time zone. */
    val workHours: com.evoapps.evoshift.domain.model.WorkHoursSettings = com.evoapps.evoshift.domain.model.WorkHoursSettings(),
    val timezone: String? = null,
    /**
     * SH-267: where a shift of the person's at another workplace of the group is (its name and
     * address), by shift id; the workplace's own shifts have none.
     */
    val places: Map<String, String> = emptyMap()
) {
    /** SH-223: remote work or a work event on [date] ("YYYY-MM-DD"), at the workplace shown. */
    fun workMarksOn(date: String): Set<com.evoapps.evoshift.domain.model.WorkRequestKind> =
        WorkMarks.ofDate(myWorkRequests.filter { it.workplaceId == currentWorkplaceId }, date)
}

sealed interface ScheduleEvent {
    data class SelectWorkplace(val workplaceId: String) : ScheduleEvent
    data class SelectRoster(val rosterId: String) : ScheduleEvent
    data class SelectDay(val day: Int) : ScheduleEvent
    data class SelectShift(val shift: Shift?) : ScheduleEvent
    data class RequestSwap(val shiftId: String, val targetUserId: String? = null, val note: String? = null) : ScheduleEvent
    /** SH-165: "Withdraw request" on the person's own waiting swap request. */
    data class WithdrawSwap(val shiftId: String) : ScheduleEvent
    /** SH-165: from a shift opened directly, the whole day's list. */
    data object ShowWholeDay : ScheduleEvent
    data class TakeShift(val shiftId: String) : ScheduleEvent
    data class UpdateActualTime(val shiftId: String, val actualTime: TimeRange?) : ScheduleEvent
    /** SH-257: the person's own hours of [shiftId] sent for a manager to confirm (reportWorkHours). */
    data class ReportWorkHours(val shiftId: String, val actualTime: TimeRange) : ScheduleEvent
    data object ClearDay : ScheduleEvent
    data object ClearError : ScheduleEvent
    /** SH-168: pull-to-refresh and Try again: the months and the shifts read again. */
    data object Refresh : ScheduleEvent
    data class ShowSwapDialog(val shiftId: String, val show: Boolean) : ScheduleEvent
}

class ScheduleViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getRosterSummaryUseCase: GetRosterSummaryUseCase,
    private val getRosterShiftsUseCase: GetRosterShiftsUseCase,
    private val getWorkplaceUseCase: GetWorkplaceUseCase,
    private val requestShiftSwapUseCase: RequestShiftSwapUseCase,
    private val takeShiftFromPoolUseCase: TakeShiftFromPoolUseCase,
    private val updateShiftUseCase: UpdateShiftUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val userRepository: UserRepository,
    private val shiftRepository: ShiftRepository,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    /** SH-89: the top bar's month; without it the newest published month. */
    private val monthSelection: WorkplaceMonthSelection? = null,
    /** SH-118: notes on the person's shifts; none without it. */
    private val shiftNotes: com.evoapps.evoshift.domain.repository.ShiftNoteRepository? = null,
    /** SH-165: "Withdraw request"; not offered without it. */
    private val cancelSwapRequest: com.evoapps.evoshift.domain.usecase.marketplace.CancelSwapRequestUseCase? = null,
    /** SH-165: the swap picker's order; without it colleagues alphabetically, all "no answer". */
    private val getSwapCandidates: com.evoapps.evoshift.domain.usecase.marketplace.GetSwapCandidatesUseCase? = null,
    /** SH-165: the person's own pay facts, for the pay change of a swap or a take; none shown without it. */
    private val getOwnPayFacts: com.evoapps.evoshift.domain.usecase.finance.GetOwnPayFactsUseCase? = null,
    private val now: () -> kotlin.time.Instant = { AppClock.now() },
    /** SH-210: the person's job interviews, shown with their shifts; none without it. */
    private val interviewRepository: com.evoapps.evoshift.domain.repository.InterviewRepository? = null,
    /** SH-223: the person's own work requests, for the remote-work mark; none without it. */
    private val workRequestRepository: com.evoapps.evoshift.domain.repository.WorkRequestRepository? = null,
    /** SH-257: "Wpisz godziny pracy" where the workplace lets the team; not offered without it. */
    private val reportWorkHours: com.evoapps.evoshift.domain.usecase.shift.ReportWorkHoursUseCase? = null
) : ViewModel() {

    private val _uiState = MutableStateFlow(ScheduleUiState())
    val uiState: StateFlow<ScheduleUiState> = _uiState.asStateFlow()
    private val rostersJob = JobSlot()
    private val shiftsJob = JobSlot()
    private val capabilitiesJob = JobSlot()
    private val notesJob = JobSlot()
    private val payFactsJob = JobSlot()
    /** SH-165: the person's own pay facts of the open month and their published shifts (all months); null without pay terms. */
    private var payFacts: Pair<com.evoapps.evoshift.domain.usecase.finance.OwnPayFacts, List<Shift>>? = null
    /** For the optimistic swap request and claim, which carry the person's name. */
    private var currentUserName = ""
    /** The workplace's swap policy, to show a swap request in its expected state before the save. */
    private var swapPolicy: SwapPolicy? = null

    /** The workplace's published months have been read (a month without one is then "none"). */
    private var rostersLoaded = false

    init {
        loadInitialUser()
        observeSession()
        observeInterviews()
        loadWorkRequests()
        monthSelection?.let { selection ->
            viewModelScope.launch { selection.month.collect { applyMonth() } }
        }
    }

    /** SH-223: the person's own work requests (the marks on their shifts). */
    private fun loadWorkRequests() {
        val repository = workRequestRepository ?: return
        viewModelScope.launch {
            val userId = getCurrentUserUseCase().first()?.uid ?: return@launch
            repository.forUser(userId).onSuccess { found -> _uiState.update { it.copy(myWorkRequests = found) } }
        }
    }

    /** SH-210: the interviews this person takes part in, kept current (cancelled ones left out). */
    private fun observeInterviews() {
        val interviews = interviewRepository ?: return
        viewModelScope.launch {
            val userId = getCurrentUserUseCase().first()?.uid ?: return@launch
            interviews.observeForParticipant(userId, "2020-01-01").collect { list ->
                _uiState.update { it.copy(myInterviews = list.filterNot { i -> i.isCancelled }) }
            }
        }
    }

    /** SH-118: the notes on this person's shifts at [workplaceId], kept current. */
    private fun observeShiftNotes(workplaceId: String) {
        val notes = shiftNotes ?: return
        notesJob.launch(viewModelScope) {
            _uiState.update { it.copy(shiftNotes = emptyMap()) }
            val userId = getCurrentUserUseCase().first()?.uid ?: return@launch
            // A failing listener (e.g. offline at first) leaves the shifts without notes.
            notes.observeFor(workplaceId, userId)
                .catch { }
                .collect { list -> _uiState.update { state -> state.copy(shiftNotes = list.associate { it.shiftId to it.note }) } }
        }
    }

    /** SH-89: shows the top bar's month: its published roster, or none. */
    private fun applyMonth() {
        val month = monthSelection?.month?.value ?: return
        if (!rostersLoaded) return
        val target = _uiState.value.publishedRosters.of(month)
        when {
            target != null -> if (target.id != _uiState.value.currentRosterId || _uiState.value.shifts.isEmpty()) selectRoster(target.id)
            else -> _uiState.update {
                it.copy(
                    currentRosterId = null,
                    isLoading = false,
                    shifts = emptyList(),
                    myShifts = emptyList(),
                    marketplaceShifts = emptyList(),
                    selectedDay = null,
                    selectedDayShifts = emptyList()
                )
            }
        }
    }

    private fun loadInitialUser() {
        viewModelScope.launch {
            val user = getCurrentUserUseCase().first()
            if (user != null) {
                currentUserName = user.displayName
                val workplaceIds =
                    user.engagements.mapValues { it.value.cachedWorkplaceName.orEmpty() }
                _uiState.update {
                    it.copy(
                        currentUserId = user.uid,
                        availableWorkplaces = workplaceIds
                    )
                }
            } else {
                _uiState.update { it.copy(error = UiText.Resource(Res.string.error_session_expired), isLoading = false) }
            }
        }
    }

    private fun observeSession() {
        viewModelScope.launch {
            sessionManager.currentWorkplaceId.collectLatest { workplaceId ->
                if (workplaceId != null) {
                    rostersLoaded = false
                    payFacts = null
                    _uiState.update {
                        it.copy(
                            currentWorkplaceId = workplaceId,
                            currentRosterId = null,
                            // Nothing of the previous workplace stays on screen: this is a first load.
                            isLoading = true,
                            publishedRosters = emptyList(),
                            shifts = emptyList(),
                            myShifts = emptyList(),
                            marketplaceShifts = emptyList(),
                            selectedDay = null,
                            selectedDayShifts = emptyList(),
                            payChanges = emptyMap()
                        )
                    }

                    capabilitiesJob.launch(viewModelScope) {
                        capabilitiesAt(getCurrentUserUseCase(), observePlanActive, workplaceId)
                            .collect { capabilities -> _uiState.update { it.copy(capabilities = capabilities) } }
                    }
                    observeShiftNotes(workplaceId)
                    // The months (and their shifts) start loading at once; the settings and the
                    // team load alongside instead of before them.
                    loadRosters(workplaceId)
                    coroutineScope {
                        val workplace = async { getWorkplaceUseCase(workplaceId).getOrNull() }
                        val employees = async { userRepository.getAssignableEmployees(workplaceId).getOrDefault(emptyList()) }
                        swapPolicy = workplace.await()?.settings?.shiftSwapPolicy
                        _uiState.update {
                            it.copy(
                                firstDayOfWeek = workplace.await()?.settings?.firstDayOfWeek ?: 1,
                                currency = workplace.await()?.settings?.currency ?: it.currency,
                                workHours = workplace.await()?.settings?.workHours ?: it.workHours,
                                timezone = workplace.await()?.settings?.timezone,
                                ownHoursByManager = workplace.await()?.let { w ->
                                    com.evoapps.evoshift.domain.model.GeofenceRules.isActive(w.settings.geofence, w.location)
                                } ?: false,
                                assignableEmployees = employees.await().filter { e -> e.uid != it.currentUserId }
                            )
                        }
                    }
                }
            }
        }
    }

    private fun loadRosters(workplaceId: String) {
        rostersJob.launch(viewModelScope) {
            _uiState.update { it.copy(isLoading = it.publishedRosters.isEmpty()) }
            getRosterSummaryUseCase(workplaceId)
                // SH-168: a failed listener: Try again when nothing is shown, else a snackbar.
                .catch { e -> failedToLoad(e) }
                .collect { rosters ->
                _uiState.update { it.copy(loadError = null, isRefreshing = it.isRefreshing && it.currentRosterId != null) }
                val published =
                    rosters.filter { it.status == RosterStatus.PUBLISHED || it.status == RosterStatus.LOCKED || it.status == RosterStatus.ARCHIVED }
                        .sortedByDescending { it.year * 100 + it.month }

                if (monthSelection != null) {
                    // SH-89: the top bar's month decides.
                    _uiState.update { it.copy(publishedRosters = published) }
                    rostersLoaded = true
                    applyMonth()
                    return@collect
                }
                // Auto-select the most recent roster if none is selected (its shifts still load)
                val autoSelect = _uiState.value.currentRosterId == null && published.isNotEmpty()
                _uiState.update { it.copy(publishedRosters = published, isLoading = autoSelect) }
                if (autoSelect) {
                    selectRoster(published.first().id)
                }
            }
        }
    }

    /** Opens a roster. Another month's shifts are cleared, so only then the spinner shows. */
    private fun selectRoster(rosterId: String) {
        val switched = _uiState.value.currentRosterId != rosterId
        // Another month's pay facts (its snapshots, its adjustments) are not this one's.
        if (switched) payFacts = null
        _uiState.update {
            it.copy(
                currentRosterId = rosterId,
                isLoading = switched || it.shifts.isEmpty(),
                shifts = if (switched) emptyList() else it.shifts,
                myShifts = if (switched) emptyList() else it.myShifts,
                marketplaceShifts = if (switched) emptyList() else it.marketplaceShifts,
                selectedDay = null,
                selectedDayShifts = emptyList(),
                payChanges = if (switched) emptyMap() else it.payChanges
            )
        }
        refreshShifts()
        loadPayFacts(rosterId)
    }

    /**
     * SH-165: reads the person's own pay facts for [rosterId] (their pay terms, their cost
     * snapshots, adjustments and absences; nobody else's), then works out the pay changes.
     */
    private fun loadPayFacts(rosterId: String) {
        val useCase = getOwnPayFacts ?: return
        if (_uiState.value.currentRosterId != rosterId) return
        payFactsJob.launch(viewModelScope) {
            val userId = _uiState.value.currentUserId.ifEmpty { getCurrentUserUseCase().first()?.uid } ?: return@launch
            val facts = runCatching { useCase(userId, rosterId) }.getOrNull()
            if (_uiState.value.currentRosterId != rosterId) return@launch
            payFacts = facts
            updatePayChanges()
        }
    }

    /** SH-165: the pay changes of the person's own shifts (given away) and the marketplace's (taken). */
    private fun updatePayChanges() {
        val (facts, published) = payFacts ?: run {
            _uiState.update { it.copy(payChanges = emptyMap()) }
            return
        }
        _uiState.update { state ->
            val rosterId = state.currentRosterId ?: return@update state.copy(payChanges = emptyMap())
            val userId = state.currentUserId
            // The open month as on screen (fresher than when the facts were read), the others as read.
            val mine = published.filter { it.rosterId != rosterId } + state.myShifts.filter { it.isPublished }
            val own = state.myShifts.filter { it.userId == userId && it.rosterId == rosterId }
                .mapNotNull { OwnMonthPayEstimate.change(userId, it, ShiftPayChangeKind.REMOVE, mine, facts) }
            val taken = state.marketplaceShifts.filter { it.rosterId == rosterId }
                .mapNotNull { OwnMonthPayEstimate.change(userId, it, ShiftPayChangeKind.ADD, mine, facts) }
            state.copy(payChanges = (own + taken).associateBy { it.shiftId })
        }
    }

    /**
     * Re-reads the current roster's shifts without a spinner of its own, so a save keeps what the
     * person sees (the open day too) and picks up what the server changed.
     */
    private fun refreshShifts() {
        val rosterId = _uiState.value.currentRosterId ?: return
        // A newer refresh replaces an older one, so reads never land out of order.
        shiftsJob.launch(viewModelScope) {
            val result = getRosterShiftsUseCase(rosterId)
            // SH-267: and the person's shifts at other workplaces of the group, lent from this one.
            val elsewhere = borrowedElsewhere(rosterId)
            // The person may have opened another month meanwhile.
            if (_uiState.value.currentRosterId != rosterId) return@launch
            result.fold(
                onSuccess = { allShifts ->
                    setShifts(allShifts + elsewhere.filter { s -> allShifts.none { it.id == s.id } })
                    _uiState.update { it.copy(isLoading = false, isRefreshing = false, loadError = null) }
                },
                onFailure = { e -> failedToLoad(e) }
            )
        }
    }

    /**
     * SH-267: the person's published shifts in [rosterId]'s month at other workplaces of the group
     * that borrow them from the workplace shown, with each workplace's name and address for
     * [ScheduleUiState.places] (they read it as its guest). None when nothing could be read.
     */
    private suspend fun borrowedElsewhere(rosterId: String): List<Shift> {
        val state = _uiState.value
        val workplaceId = state.currentWorkplaceId ?: return emptyList()
        val userId = state.currentUserId.ifEmpty { return emptyList() }
        val (year, month) = RosterIds.yearMonthOf(rosterId)
        val monthId = "$year-${month.toString().padStart(2, '0')}"
        val shifts = runCatching { shiftRepository.observeShiftsForUser(userId, "$monthId-01").first() }.getOrDefault(emptyList())
            .filter { it.borrowedFromWorkplaceId == workplaceId && it.rosterId != rosterId && it.date.startsWith(monthId) && it.isPublished }
        if (shifts.isEmpty()) return emptyList()
        val places = shifts.map { RosterIds.workplaceIdOf(it.rosterId) }.distinct().mapNotNull { id ->
            getWorkplaceUseCase(id).getOrNull()?.let { wp -> id to listOf(wp.name, wp.address).filter { it.isNotBlank() }.joinToString(", ") }
        }.toMap()
        _uiState.update { it.copy(places = it.places + shifts.mapNotNull { s -> places[RosterIds.workplaceIdOf(s.rosterId)]?.let { s.id to it } }) }
        return shifts
    }

    /** Sets the roster's shifts and everything derived from them (mine, the marketplace, the open day). */
    private fun setShifts(allShifts: List<Shift>) {
        _uiState.update { state ->
            val userId = state.currentUserId
            state.copy(
                shifts = allShifts,
                // SH-136: the shifts they work, also those recorded with them as who worked them.
                myShifts = allShifts.filter { it.workerId == userId }.sortedBy { it.plannedTime.start },
                marketplaceShifts = allShifts.filter {
                    it.swapRequest?.status == SwapStatus.AVAILABLE && it.userId != userId
                }.sortedBy { it.plannedTime.start },
                selectedDayShifts = state.selectedDay?.let { dayShifts(allShifts, it, userId) } ?: state.selectedDayShifts
            )
        }
        if (payFacts != null) updatePayChanges()
    }

    /** SH-168: nothing on screen yet: a load error (Try again); over shifts already shown, a snackbar. */
    private fun failedToLoad(e: Throwable) {
        _uiState.update {
            if (it.shifts.isEmpty()) it.copy(loadError = e.toUiText(), isLoading = false, isRefreshing = false)
            else it.copy(error = e.toUiText(), isLoading = false, isRefreshing = false)
        }
    }

    /** SH-168: the workplace's months and the open month's shifts read again. */
    private fun refresh() {
        val workplaceId = _uiState.value.currentWorkplaceId ?: return
        _uiState.update { it.copy(isRefreshing = true, loadError = null, isLoading = it.shifts.isEmpty()) }
        loadRosters(workplaceId)
        // Without a month open (none published for it), the months' answer ends the refresh.
        _uiState.value.currentRosterId?.let { rosterId ->
            refreshShifts()
            loadPayFacts(rosterId)
        }
    }

    /** Undoes an optimistic change whose save failed, and says why. */
    private fun rollBack(before: List<Shift>, e: Throwable) {
        setShifts(before)
        _uiState.update { it.copy(error = e.toUiText()) }
    }

    fun onEvent(event: ScheduleEvent) {
        when (event) {
            is ScheduleEvent.SelectWorkplace -> sessionManager.setWorkplaceId(event.workplaceId)
            // With the shared month (SH-89) picking a roster picks its month everywhere.
            is ScheduleEvent.SelectRoster -> monthSelection?.let { selection ->
                WorkplaceMonthSelection.monthOf(event.rosterId)?.let(selection::selectMonth)
            } ?: selectRoster(event.rosterId)
            is ScheduleEvent.SelectDay -> updateSelectedDay(event.day)
            is ScheduleEvent.SelectShift -> _uiState.update {
                // SH-165: closing a shift opened straight from the calendar closes its day too.
                if (event.shift == null && it.dayListHidden) it.copy(selectedShift = null, selectedDay = null, selectedDayShifts = emptyList(), dayListHidden = false)
                else it.copy(selectedShift = event.shift, dayListHidden = if (event.shift == null) false else it.dayListHidden)
            }
            ScheduleEvent.ShowWholeDay -> _uiState.update { it.copy(selectedShift = null, dayListHidden = false) }
            is ScheduleEvent.RequestSwap -> if (canTrade()) requestSwap(event.shiftId, event.targetUserId, event.note)
            is ScheduleEvent.WithdrawSwap -> if (canTrade()) withdrawSwap(event.shiftId)
            is ScheduleEvent.TakeShift -> if (canTrade()) takeShift(event.shiftId)
            is ScheduleEvent.UpdateActualTime -> if (canTrade()) updateActualTime(event.shiftId, event.actualTime)
            is ScheduleEvent.ReportWorkHours -> if (canTrade()) reportHours(event.shiftId, event.actualTime)
            ScheduleEvent.ClearDay -> _uiState.update {
                it.copy(
                    selectedDay = null,
                    selectedDayShifts = emptyList()
                )
            }

            ScheduleEvent.ClearError -> _uiState.update { it.copy(error = null, message = null) }
            ScheduleEvent.Refresh -> refresh()
            is ScheduleEvent.ShowSwapDialog -> {
                _uiState.update {
                    it.copy(
                        showSwapDialog = event.show, swapTargetShiftId = if (event.show) event.shiftId else null,
                        swapCandidates = null, swapRole = null
                    )
                }
                if (event.show) loadSwapCandidates(event.shiftId)
            }
        }
    }

    private fun canTrade(): Boolean = _uiState.value.capabilities.shows(Gate.TRADE_SHIFTS)

    /**
     * SH-165: whether the person may correct their own hours on [shift]: only once it has started
     * (before that the hours block would just repeat the plan above the swap button).
     */
    fun hoursOpen(shift: Shift): Boolean {
        val nowLocal = now().toLocalDateTime(kotlinx.datetime.TimeZone.currentSystemDefault()).toString()
        return shift.plannedTime.start.isNotEmpty() && nowLocal >= shift.plannedTime.start
    }

    /** SH-165: whether [shift]'s swap request can be withdrawn by the person ("Withdraw request"). */
    fun canWithdraw(shift: Shift): Boolean {
        val request = shift.swapRequest ?: return false
        return cancelSwapRequest != null && request.initiatorUserId == _uiState.value.currentUserId &&
            request.status in com.evoapps.evoshift.domain.usecase.marketplace.CancelSwapRequestUseCase.WITHDRAWABLE
    }

    private fun loadSwapCandidates(shiftId: String) {
        val state = _uiState.value
        val shift = state.shifts.find { it.id == shiftId } ?: return
        val role = shift.requiredRole?.takeIf { it.isNotBlank() }
        viewModelScope.launch {
            val ranked = getSwapCandidates?.invoke(shift, state.assignableEmployees, state.shifts)
                ?: com.evoapps.evoshift.domain.usecase.marketplace.GetSwapCandidatesUseCase.rank(shift, state.assignableEmployees, state.shifts, emptyMap())
            // The dialog may have been closed or opened for another shift meanwhile.
            if (_uiState.value.swapTargetShiftId == shiftId) _uiState.update { it.copy(swapCandidates = ranked, swapRole = role) }
        }
    }

    private fun withdrawSwap(shiftId: String) {
        val cancel = cancelSwapRequest ?: return
        val before = _uiState.value.shifts
        setShifts(before.map { if (it.id == shiftId) it.copy(swapRequest = null) else it })
        _uiState.update { state -> state.copy(selectedShift = state.selectedShift?.takeIf { it.id == shiftId }?.copy(swapRequest = null) ?: state.selectedShift) }
        viewModelScope.launch {
            cancel(shiftId, _uiState.value.currentUserId).fold(
                onSuccess = {
                    _uiState.update { it.copy(message = UiText.Resource(Res.string.schedule_swap_withdrawn)) }
                    refreshShifts()
                },
                onFailure = { e ->
                    rollBack(before, e)
                    _uiState.update { state -> state.copy(selectedShift = state.selectedShift?.let { s -> before.find { it.id == s.id } ?: s }) }
                }
            )
        }
    }

    private fun requestSwap(shiftId: String, targetUserId: String?, note: String?) {
        val state = _uiState.value
        val before = state.shifts
        // Mirrors RequestShiftSwapUseCase's starting status; the refresh after the save brings the real request.
        val status = if (targetUserId == null && (swapPolicy == SwapPolicy.A_DIRECT || swapPolicy == SwapPolicy.B_APPROVAL)) {
            SwapStatus.AVAILABLE
        } else {
            SwapStatus.REQUESTED_BY_OWNER
        }
        val request = SwapRequest(
            status = status,
            initiatorUserId = state.currentUserId,
            initiatorName = currentUserName,
            candidateUserId = targetUserId,
            candidateName = targetUserId?.let { id -> state.assignableEmployees.find { it.uid == id }?.displayName },
            managerNote = null,
            createdAt = AppClock.now().toString(),
            initiatorNote = note?.trim()?.takeIf { it.isNotEmpty() }
        )
        setShifts(before.map { if (it.id == shiftId) it.copy(swapRequest = request) else it })
        _uiState.update { it.copy(showSwapDialog = false, selectedShift = null) }
        viewModelScope.launch {
            requestShiftSwapUseCase(shiftId, state.currentUserId, targetUserId, note).fold(
                onSuccess = {
                    AppTelemetry.log(AnalyticsEvent.ShiftSwapRequested)
                    refreshShifts()
                },
                onFailure = { e -> rollBack(before, e) }
            )
        }
    }

    private fun takeShift(shiftId: String) {
        val userId = _uiState.value.currentUserId
        val before = _uiState.value.shifts
        // Off the marketplace at once, as a claim awaiting approval until the server says how it went.
        setShifts(before.map {
            if (it.id == shiftId) it.copy(
                swapRequest = it.swapRequest?.copy(status = SwapStatus.TAKEN_PENDING, candidateUserId = userId, candidateName = currentUserName)
            ) else it
        })
        _uiState.update { it.copy(selectedShift = null) }
        viewModelScope.launch {
            takeShiftFromPoolUseCase(shiftId, userId).fold(
                onSuccess = { outcome ->
                    AppTelemetry.log(AnalyticsEvent.ShiftTaken)
                    if (outcome == ClaimOutcome.ASSIGNED) {
                        setShifts(_uiState.value.shifts.map {
                            if (it.id == shiftId) it.copy(userId = userId, userDisplayName = currentUserName, isOpen = false, swapRequest = null) else it
                        })
                    }
                    refreshShifts()
                    val confirmation = if (outcome == ClaimOutcome.ASSIGNED) Res.string.claim_assigned else Res.string.claim_pending
                    _uiState.update { it.copy(message = UiText.Resource(confirmation)) }
                },
                onFailure = { e -> rollBack(before, e) }
            )
        }
    }

    /**
     * SH-257: whether the person may enter their own hours of [shift] ("Wpisz godziny pracy"): the
     * workplace lets the team, it is theirs, of today or the days the workplace allows, started,
     * and no manager confirmed it yet (the reportWorkHours function checks the same).
     */
    fun selfEntryOpen(shift: Shift): Boolean {
        val state = _uiState.value
        if (reportWorkHours == null || !state.workHours.selfEntry) return false
        val zone = com.evoapps.evoshift.domain.usecase.timeZoneOrNull(state.timezone) ?: kotlinx.datetime.TimeZone.currentSystemDefault()
        val local = now().toLocalDateTime(zone)
        val nowLocal = local.toString().let { if (it.length == 16) "$it:00" else it.take(19) }
        return com.evoapps.evoshift.domain.model.WorkHoursRules.selfEntryOpen(shift, state.currentUserId, state.workHours, local.date, nowLocal)
    }

    /** SH-257: optimistic: the entered hours show on the shift at once; a refusal puts it back and says why. */
    private fun reportHours(shiftId: String, actualTime: TimeRange) {
        val useCase = reportWorkHours ?: return
        val before = _uiState.value.shifts
        val shift = before.firstOrNull { it.id == shiftId } ?: return
        if (!selfEntryOpen(shift)) return
        val stamp = com.evoapps.evoshift.domain.model.ClockStamp(com.evoapps.evoshift.domain.model.ClockSource.REPORTED)
        val shown = shift.copy(
            actualTime = actualTime,
            clockAudit = com.evoapps.evoshift.domain.model.ClockAudit(stamp, stamp, reportedBy = _uiState.value.currentUserId, reportedAt = now().toString())
        )
        setShifts(before.map { if (it.id == shiftId) shown else it })
        _uiState.update { it.copy(selectedShift = null) }
        viewModelScope.launch {
            useCase(shiftId, actualTime.start, actualTime.end).fold(
                onSuccess = {
                    _uiState.update { it.copy(message = UiText.Resource(Res.string.schedule_hours_sent)) }
                    refreshShifts()
                },
                onFailure = { e -> rollBack(before, e) }
            )
        }
    }

    private fun updateActualTime(shiftId: String, actualTime: TimeRange?) {
        val before = _uiState.value.shifts
        setShifts(before.map { if (it.id == shiftId) it.copy(actualTime = actualTime) else it })
        _uiState.update { it.copy(selectedShift = null) }
        viewModelScope.launch {
            // Only the actual times: what Firestore rules let the assignee change on their shift.
            shiftRepository.updateActualTime(shiftId, actualTime, _uiState.value.currentUserId).fold(
                onSuccess = { refreshShifts() },
                onFailure = { e -> rollBack(before, e) }
            )
        }
    }

    /**
     * Opens [day]. SH-165: a day with exactly one shift of the person's own opens that shift
     * directly; its day's list stays behind it ("Show the whole day").
     */
    private fun updateSelectedDay(day: Int) {
        _uiState.update {
            val shifts = dayShifts(it.shifts, day, it.currentUserId)
            val own = shifts.filter { s -> s.workerId == it.currentUserId }
            it.copy(
                selectedDay = day,
                selectedDayShifts = shifts,
                selectedShift = own.singleOrNull() ?: it.selectedShift,
                dayListHidden = own.size == 1
            )
        }
    }

    /** The day's shifts: the person's own first, then chronologically. */
    private fun dayShifts(allShifts: List<Shift>, day: Int, userId: String): List<Shift> {
        // Find shifts for the day. Date format is YYYY-MM-DD
        val dayString = day.toString().padStart(2, '0')
        return allShifts.filter { it.date.endsWith("-$dayString") }
            .sortedWith(compareBy<Shift> { it.workerId != userId }.thenBy { it.plannedTime.start })
    }
}
